import CloseIcon from '@mui/icons-material/Close';
import { Accordion, AccordionDetails, AccordionSummary, TableCell, TableRow } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';

import {
  EmailBodyContentTypeDto,
  EmailSummaryDto,
  ResourceTimelineMessageListItemDto,
} from '@/api/generated';
import { IconButtonWithColors } from '@/components/IconButtonWithColors.comp.tsx';
import { CustomTableEventModel } from '@/components/table/model/CutomTableEvent.model.ts';
import { customTableEventDispatcher } from '@/components/table/util/customTableEventDispatcher.util.ts';
import { useRemoveThreadFromResource } from '@/components/timelineMessages/comp/tableRow/components/email/util/useRemoveThreadFromResource.util.ts';
import { ResourceTimelineMessagesTableRowMissingData } from '@/components/timelineMessages/comp/tableRow/components/ResourceTimelineMessagesTableRowMissingData.comp.tsx';
import { CommonDialog } from '@/components/ui/deleteDialog/CommonDialog.comp.tsx';
import { DeleteDialog } from '@/components/ui/deleteDialog/DeleteDialog.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { EmptyFunction } from '@/models/EmptyFunction.model.ts';
import { returnPromise } from '@/utils/returnPromise.util.ts';
import { SanitizeHTML } from '@/utils/SanitzeHTML.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { CollapsibleEmail } from '@/views/emails/components/CollapsibleEmail.comp.tsx';
import { EmailSummary } from '@/views/emails/components/EmailSummary.comp.tsx';
import { EmailSummaryAttachmentsAndDate } from '@/views/emails/components/EmailSummaryAttachmentsAndDate.comp.tsx';
import { EmailSummaryBodyPreview } from '@/views/emails/components/EmailSummaryBodyPreview.comp.tsx';
import { ThreadEmailsItemAttachments } from '@/views/emails/components/threadEmails/components/attachments/ThreadEmailsItemAttachments.comp.tsx';
import { useDeleteEmail } from '@/views/emails/components/threadsList/components/body/util/useDeleteEmail.util.ts';
import { useMarkEmailAsRead } from '@/views/emails/components/threadsList/components/body/util/useMarkEmailAsRead.util.ts';
import { EmailControlButtons } from '@/views/emails/components/threadsList/components/EmailControlButtons.comp.tsx';
import { useControlButtonsActions } from '@/views/emails/components/threadsList/util/useControlButtonsActions.util.ts';

type Props = {
  row: ResourceTimelineMessageListItemDto;
};

export const ResourceTimelineMessagesTableRowEmail: React.FC<Props> = ({ row }) => {
  const [removeEmailDialog, setRemoveEmailDialog] = useState(false);
  const [deleteEmailDialog, setDeleteEmailDialog] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRead, setIsRead] = useState(row.email?.isRead ?? true);
  const { deleteEmail } = useDeleteEmail();
  const { markEmailRead } = useMarkEmailAsRead();

  const accordionSx = useMemo(
    () => ({
      borderLeft: '3px solid',
      borderLeftColor: isRead ? 'white' : themeColors.accentColor,
    }),
    [isRead],
  );

  const { email, type } = row;

  if (!email) {
    return <ResourceTimelineMessagesTableRowMissingData type={type} />;
  }

  const handleOpenRemoveEmailDialog = () => {
    setRemoveEmailDialog(true);
  };
  const handleCloseRemoveEmailDialog = () => {
    setRemoveEmailDialog(false);
  };

  const handleOpenDeleteEmailDialog = () => {
    setDeleteEmailDialog(true);
  };

  const handleCloseDeleteEmailDialog = () => {
    setDeleteEmailDialog(false);
  };

  const handleDeleteEmail = async () => {
    await deleteEmail({ emailId: email.emailId, threadId: email.threadId });
    await returnPromise(null, 500);
    customTableEventDispatcher(CustomTableEventModel.REFRESH);
  };

  const handleSummaryClick = async () => {
    if (!isExpanded && !isRead) {
      await markEmailRead({ emailId: email.emailId, threadId: email.threadId });
      setIsRead(true);
    }
    setIsExpanded(cur => !cur);
  };

  const emailWithReadStatus = { ...email, isRead };

  return (
    <TableRow sx={{ border: `1px solid ${themeColors.stroke}`, overflowX: 'clip' }}>
      <TableCell sx={{ p: 0 }}>
        <Accordion disableGutters expanded={isExpanded} sx={accordionSx}>
          <AccordionSummary
            onClick={handleSummaryClick}
            sx={{ '& .MuiAccordionSummary-content': { display: 'block', width: '100%' } }}
          >
            <EmailHeader
              email={emailWithReadStatus}
              handleOpenDeleteEmailDialog={handleOpenDeleteEmailDialog}
              handleOpenRemoveEmailDialog={handleOpenRemoveEmailDialog}
            />
            {!isExpanded && <EmailSummaryBodyPreview email={emailWithReadStatus} />}
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              component="div"
              sx={{ color: themeColors.greyDarker, maxWidth: '100%', overflowX: 'auto', pb: 1 }}
              variant="f12Regular"
            >
              {email.bodyContentType === EmailBodyContentTypeDto.Html ? (
                <CollapsibleEmail rawHtml={email.body} />
              ) : (
                email.body
              )}
            </Typography>
            <ThreadEmailsItemAttachments attachments={email.attachments} emailId={email.emailId} />
          </AccordionDetails>
        </Accordion>
      </TableCell>
      {removeEmailDialog && (
        <RemoveThreadFromProcessDialog
          email={email}
          handleCloseRemoveEmailDialog={handleCloseRemoveEmailDialog}
          row={row}
        />
      )}
      {deleteEmailDialog && (
        <DeleteDialog
          name={<SanitizeHTML html={email.bodyPreview} />}
          onCancel={handleCloseDeleteEmailDialog}
          onConfirm={handleDeleteEmail}
        />
      )}
    </TableRow>
  );
};

type DialogProps = {
  email: EmailSummaryDto;
  handleCloseRemoveEmailDialog: EmptyFunction;
  row: ResourceTimelineMessageListItemDto;
};

const RemoveThreadFromProcessDialog: React.FC<DialogProps> = ({
  email,
  handleCloseRemoveEmailDialog,
  row,
}) => {
  const { t } = useTranslationWithPrefix('components.timelineMessages');
  const { removeThreadFromResource } = useRemoveThreadFromResource();

  const handleRemoveEmail = async () => {
    await removeThreadFromResource({
      emailThreadId: email.threadId,
      resource: row.resourceId,
    });
    handleCloseRemoveEmailDialog();
  };

  return (
    <CommonDialog
      cancelButtonLabel={t('dialog.removeThreadFromProcess.button.cancel')}
      confirmButtonLabel={t('dialog.removeThreadFromProcess.button.confirm')}
      content={
        <Stack spacing={2}>
          <Typography component="div">{t('dialog.removeThreadFromProcess.content')}</Typography>
          <Divider />
          <SanitizeHTML html={email.bodyPreview} />
        </Stack>
      }
      icon={<CloseIcon htmlColor={themeColors.red} />}
      onCancel={handleCloseRemoveEmailDialog}
      onConfirm={handleRemoveEmail}
      title={t('dialog.removeThreadFromProcess.title')}
    />
  );
};

type EmailHeaderProps = {
  email: EmailSummaryDto;
  handleOpenDeleteEmailDialog: EmptyFunction;
  handleOpenRemoveEmailDialog: EmptyFunction;
};

const EmailHeader: React.FC<EmailHeaderProps> = ({
  email,
  handleOpenDeleteEmailDialog,
  handleOpenRemoveEmailDialog,
}) => {
  const { t } = useTranslationWithPrefix('components.timelineMessages');
  const { handleForward, handleReply, handleReplyAll } = useControlButtonsActions();
  const { externalUserId } = useUserContext();

  return (
    <Grid container spacing={1} sx={{ pb: 1, width: '100%' }}>
      <Grid size="grow">
        <EmailSummary email={email} fullDetails isThreadEmailsList openInFloatingMode showTopic />
      </Grid>
      <Grid size={3}>
        <Stack spacing={1}>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <EmailControlButtons
              emailOwnerId={email.ownerExternalUserId}
              isDraft={email.isDraft}
              onDelete={email.isDraft ? handleOpenDeleteEmailDialog : undefined}
              onForward={handleForward({ email, openInFloatingMode: true })}
              onReply={
                email.ownerExternalUserId === externalUserId
                  ? handleReply({ email, openInFloatingMode: true })
                  : undefined
              }
              onReplyAll={handleReplyAll({ email, openInFloatingMode: true })}
            />
            <IconButtonWithColors
              color={themeColors.red}
              hoverColor={themeColors.redDark}
              icon={<CloseIcon />}
              onClick={handleOpenRemoveEmailDialog}
              tooltip={t('tooltip.removeThreadFromProcess')}
            />
          </Stack>
          <EmailSummaryAttachmentsAndDate email={email} />
        </Stack>
      </Grid>
    </Grid>
  );
};
